import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AiOutlineSend } from "react-icons/ai";
import { toast } from "react-toastify";
import { format } from "date-fns";
import {
  getCustomerConversation,
  sendCustomerMessage,
} from "../../../api/customerSupport";
import {
  CustomerSupportSocketProvider,
  useCustomerSupportSocket,
} from "../contexts/CustomerSupportSocketContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import onelykLogo from "../../../Assets/onelyklogo.svg";

const useChatScroll = (dep) => {
  const ref = useRef();
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [dep]);
  return ref;
};

const MessageBubble = ({ message }) => {
  const isCustomer = message.senderType === "customer";
  const isAi = message.senderType === "ai";

  return (
    <div className={`flex ${isCustomer ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-3.5 py-2.5 sm:px-4 ${
          isCustomer
            ? "bg-indigo-600 text-white rounded-br-sm"
            : isAi
              ? "bg-gray-100 text-gray-800 rounded-bl-sm"
              : "bg-emerald-100 text-gray-800 rounded-bl-sm"
        }`}
      >
        {!isCustomer && (
          <p className="text-xs font-medium mb-1 opacity-70">Support Team</p>
        )}
        <p className="text-sm sm:text-base whitespace-pre-wrap break-words">
          {message.content}
        </p>
        <p
          className={`text-[10px] mt-1 ${isCustomer ? "text-indigo-200" : "text-gray-400"}`}
        >
          {format(new Date(message.createdAt), "h:mm a")}
        </p>
      </div>
    </div>
  );
};

const CustomerChatInner = ({ code }) => {
  const { socket } = useCustomerSupportSocket();
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useChatScroll(messages);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getCustomerConversation(code);
        setMessages(data.messages || []);
        setConversation(data.conversation);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Invalid or expired chat link",
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  useEffect(() => {
    if (!socket) return;

    const onNewMessage = (payload) => {
      if (payload.conversationId !== conversation?._id) return;
      setMessages((prev) => {
        const exists = prev.some((m) => m._id === payload.message._id);
        if (exists) return prev;
        return [...prev, payload.message];
      });
    };

    const onTyping = (payload) => {
      if (payload.senderType !== "customer") {
        setIsTyping(payload.isTyping);
      }
    };

    const onConversationUpdated = (payload) => {
      if (payload.conversation) {
        setConversation((prev) => ({ ...prev, ...payload.conversation }));
      }
    };

    socket.on("supportChat:newMessage", onNewMessage);
    socket.on("supportChat:typing", onTyping);
    socket.on("supportChat:conversationUpdated", onConversationUpdated);

    return () => {
      socket.off("supportChat:newMessage", onNewMessage);
      socket.off("supportChat:typing", onTyping);
      socket.off("supportChat:conversationUpdated", onConversationUpdated);
    };
  }, [socket, conversation?._id]);

  const handleSend = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || sending) return;

    setSending(true);
    setInput("");
    try {
      const result = await sendCustomerMessage(code, content);
      setMessages((prev) => {
        const updated = [...prev];
        if (
          result.customerMessage &&
          !updated.some((m) => m._id === result.customerMessage._id)
        ) {
          updated.push(result.customerMessage);
        }
        return updated;
      });
      setConversation((prev) => ({
        ...prev,
        requiresHumanAttention: true,
        status: "waiting_for_human",
      }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send message");
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  const handleTyping = (value) => {
    setInput(value);
    if (socket) {
      socket.emit("supportChat:typing", {
        conversationId: conversation?._id,
        isTyping: value.length > 0,
      });
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-50 pt-safe">
      <header className="shrink-0 bg-indigo-600 text-white px-4 py-3 sm:py-4 flex items-center gap-3 shadow-md">
        <img
          src={onelykLogo}
          alt="Onelyk"
          className="h-7 sm:h-8 brightness-0 invert shrink-0"
        />

      </header>

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3 sm:py-4 hide-scrollbar"
      >
        {messages.map((msg) => (
          <MessageBubble key={msg._id} message={msg} />
        ))}
        {isTyping && (
          <div className="flex justify-start mb-3">
            <div className="bg-gray-100 rounded-2xl px-4 py-2 text-sm text-gray-500">
              typing...
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSend}
        className="shrink-0 border-t bg-white px-3 sm:px-4 py-2.5 sm:py-3 flex items-end gap-2 pb-safe"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => handleTyping(e.target.value)}
          placeholder="Type your message..."
          className="flex-1 min-w-0 rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          disabled={sending}
          autoComplete="off"
          enterKeyHint="send"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="shrink-0 rounded-full bg-indigo-600 p-3 sm:p-2.5 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 touch-manipulation"
          aria-label="Send message"
        >
          <AiOutlineSend size={20} className="sm:w-[18px] sm:h-[18px]" />
        </button>
      </form>
    </div>
  );
};

const CustomerChat = () => {
  const { token: code } = useParams();

  if (!code) {
    return (
      <div className="fixed inset-0 flex items-center justify-center text-gray-500 px-4">
        Invalid chat link
      </div>
    );
  }

  return (
    <CustomerSupportSocketProvider
      authType="customer"
      conversationToken={code}
    >
      <CustomerChatInner code={code} />
    </CustomerSupportSocketProvider>
  );
};

export default CustomerChat;
